import { Children, cloneElement, Component, isValidElement, ReactElement, ReactNode } from 'react';
import { get } from 'lodash';
import noop from 'lodash/noop';

import thematize from 'lib/thematize';
import styles from './Container.scss';

interface Props {
  children: ReactNode;
  button?: ReactNode;
  containerClassName?: string;
  contentClassName?: string;
  headerClassName?: string;
  home?: boolean;
  nested?: boolean;
  onTabSelect?: (anchor: string) => void;
  progress?: boolean;
  selectedTab?: string;
  tabsClassName?: string;
}

interface State {
  activeTabIndex: number;
}

class TabsContainer extends Component<Props, State> {
  static defaultProps = {
    nested: false,
    home: false,
    progress: false,
    onTabSelect: noop,
    containerClassName: '',
    headerClassName: '',
    contentClassName: '',
    tabsClassName: '',
  };

  static validChildren = (children: ReactNode) =>
    Children.toArray(children).filter((child) => isValidElement(child));

  static findActiveTabIndex(children: ReactNode, selectedTab?: string): number {
    let index = 0;

    TabsContainer.validChildren(children as ReactElement).forEach((child, i) => {
      if (`#${get(child, 'props.id')}` === selectedTab) {
        index = i;
      }
    });

    return index;
  }

  state = {
    activeTabIndex: TabsContainer.findActiveTabIndex(this.props.children, this.props.selectedTab),
  };

  theme = thematize(styles);

  componentDidUpdate(prevProps: Props): void {
    if (this.props.selectedTab !== prevProps.selectedTab) {
      this.setState({
        activeTabIndex: TabsContainer.findActiveTabIndex(
          this.props.children,
          this.props.selectedTab
        ),
      });
    }
  }

  handleTabClick = (activeTabIndex: number, id: string): void => {
    this.setState({ activeTabIndex });
    (this.props.onTabSelect as (anchor: string) => void)(`#${id}`);
  };

  renderTabs() {
    return TabsContainer.validChildren(this.props.children).map((child, i) =>
      cloneElement(get(child as ReactElement, 'props.title'), {
        key: get(child, 'props.id'),
        active: this.state.activeTabIndex === i,
        done: this.props.progress && this.state.activeTabIndex > i,
        onClick: () => {
          this.handleTabClick(i, get(child as ReactElement, 'props.id'));
          get(child, 'props.title.props.onClick', noop)();
        },
        nested: this.props.nested,
        home: this.props.home,
        progress: this.props.progress,
      })
    );
  }

  renderContent(): ReactElement | null {
    const activeTab = Children.toArray(this.props.children).filter((child) =>
      isValidElement(child)
    )[this.state.activeTabIndex];

    if (!activeTab) {
      return null;
    }

    return get(activeTab as ReactElement, 'props.content');
  }

  render(): ReactElement {
    const {
      nested,
      home,
      progress,
      button,
      containerClassName,
      headerClassName,
      contentClassName,
      tabsClassName,
    } = this.props;

    return (
      <div
        className={`${this.theme('container', { nested, home, progress })} ${containerClassName}`}
      >
        <div className={`${this.theme('header', { home, progress })} ${headerClassName}`}>
          <div className={`${this.theme('header__tabs', { home, progress })} ${tabsClassName}`}>
            {this.renderTabs()}
          </div>

          <div className={this.theme('header__actions', { home, progress })}>{button}</div>
        </div>

        <div className={`${this.theme('content', { home, progress })} ${contentClassName}`}>
          {this.renderContent()}
        </div>
      </div>
    );
  }
}

export default TabsContainer;
